<template>
  <div>
    <slot :open="handleCallClick" />

    <Modal
      v-if="showConfirmModal"
      ref="modalRef"
      :title="getModalTitle()"
      :body="getModalBody()"
      :action="getModalAction()"
      :action-color="getModalActionColor()"
      :cancel-only="callState === 'active'"
      :disable-click-outside="true"
      @close-modal="onModalClosed"
      @handle-action="handleModalAction"
    >
      <div class="mt-4 space-y-3">
        <div class="bg-blue-50 rounded-md p-3">
          <div class="flex items-center">
            <svg class="h-5 w-5 text-blue-400 mr-2" fill="none" viewBox="0 0 24 24" stroke="currentColor">
              <path
                stroke-linecap="round"
                stroke-linejoin="round"
                stroke-width="2"
                d="M2.25 6.75c0 8.284 6.716 15 15 15h2.25a2.25 2.25 0 002.25-2.25v-1.372c0-.516-.351-.966-.852-1.091l-4.423-1.106c-.44-.11-.902.055-1.173.417l-.97 1.293c-.282.376-.769.542-1.21.38a12.035 12.035 0 01-7.143-7.143c-.162-.441.004-.928.38-1.21l1.293-.97c.363-.271.527-.734.417-1.173L6.963 3.102a1.125 1.125 0 00-1.091-.852H4.5A2.25 2.25 0 002.25 4.5v2.25z"
              />
            </svg>
            <span class="text-sm font-medium text-blue-800">Call Details</span>
          </div>
          <div class="mt-2 text-sm text-blue-700">
            <div class="mb-1">
              <span class="font-medium">Contact:</span>
              <span class="ml-2">{{ props.contactName || 'Unknown' }}</span>
            </div>
            <div>
              <span class="font-medium">Phone:</span>
              <span class="ml-2">{{ formattedNumber }}</span>
            </div>
          </div>
        </div>

        <!-- Audio Device Selection (only show before call starts) -->
        <div v-if="callState === 'idle'" class="space-y-4">
          <!-- Speaker Selection -->
          <div class="bg-gray-50 rounded-md p-4 border border-gray-200">
            <label class="text-sm font-medium text-gray-700 mb-2 block">Speaker</label>
            <div class="flex items-center space-x-2">
              <Select v-model="selectedSpeakerId" class="flex-1">
                <SelectTrigger>
                  <SelectValue placeholder="Select speaker" />
                </SelectTrigger>
                <SelectContent>
                  <SelectItem v-for="device in audioOutputs" :key="device.deviceId" :value="device.deviceId">
                    {{ device.label }}
                  </SelectItem>
                </SelectContent>
              </Select>
              <Button variant="outline" size="sm" @click="testSpeaker">
                <svg class="w-4 h-4" fill="none" viewBox="0 0 24 24" stroke="currentColor">
                  <path
                    stroke-linecap="round"
                    stroke-linejoin="round"
                    stroke-width="2"
                    d="M15.536 8.464a5 5 0 010 7.072m2.828-9.9a9 9 0 010 12.728M5.586 15H4a1 1 0 01-1-1v-4a1 1 0 011-1h1.586l4.707-4.707C10.923 3.663 12 4.109 12 5v14c0 .891-1.077 1.337-1.707.707L5.586 15z"
                  />
                </svg>
                Test
              </Button>
            </div>
          </div>

          <!-- Microphone Selection -->
          <div class="bg-gray-50 rounded-md p-4 border border-gray-200">
            <label class="text-sm font-medium text-gray-700 mb-2 block">Microphone</label>
            <Select v-model="selectedMicrophoneId" class="mb-3">
              <SelectTrigger>
                <SelectValue placeholder="Select microphone" />
              </SelectTrigger>
              <SelectContent>
                <SelectItem v-for="device in audioInputs" :key="device.deviceId" :value="device.deviceId">
                  {{ device.label }}
                </SelectItem>
              </SelectContent>
            </Select>

            <!-- Microphone Level Indicator -->
            <div v-if="isMicrophoneLevelActive" class="space-y-2">
              <div class="flex items-center justify-between">
                <span class="text-xs text-gray-600">Microphone Level</span>
                <span class="text-xs text-gray-500">{{ Math.round(microphoneLevel) }}%</span>
              </div>
              <div class="w-full bg-gray-200 rounded-full h-2 overflow-hidden">
                <div
                  class="h-full transition-all duration-100 ease-out"
                  :class="{
                    'bg-green-500': microphoneLevel > 0 && microphoneLevel <= 70,
                    'bg-yellow-500': microphoneLevel > 70 && microphoneLevel <= 90,
                    'bg-red-500': microphoneLevel > 90,
                  }"
                  :style="{ width: `${microphoneLevel}%` }"
                ></div>
              </div>
            </div>
          </div>
        </div>

        <div
          v-if="callState === 'connecting' || callState === 'ringing'"
          class="bg-yellow-50 rounded-md p-3 border border-yellow-200"
        >
          <div class="flex items-center">
            <div class="w-2 h-2 bg-yellow-400 rounded-full mr-2 animate-pulse"></div>
            <span class="text-sm font-medium text-yellow-800">{{
              callState === 'connecting' ? 'Connecting...' : 'Ringing...'
            }}</span>
          </div>
          <p class="text-sm text-yellow-700 mt-1">
            Calling {{ formattedNumber }}. Please wait while we connect your call.
          </p>
        </div>

        <div v-if="callState === 'active'" class="bg-green-50 rounded-md p-3 border border-green-200">
          <div class="flex items-center">
            <div class="w-2 h-2 bg-green-400 rounded-full mr-2 animate-pulse"></div>
            <span class="text-sm font-medium text-green-800">Call Connected</span>
          </div>
          <p class="text-sm text-green-700 mt-1">
            Your call is currently active. Use the controls below to manage the call.
          </p>
        </div>

        <div v-if="callState === 'ended'" class="bg-gray-50 rounded-md p-3 border border-gray-200">
          <div class="flex items-center">
            <div class="w-2 h-2 bg-gray-400 rounded-full mr-2"></div>
            <span class="text-sm font-medium text-gray-800">Call Ended</span>
          </div>
          <p class="text-sm text-gray-700 mt-1">
            The call has ended. Your call recording will be available for download shortly.
          </p>
        </div>

        <div
          v-if="callState === 'ended' && callDurationSeconds"
          class="bg-blue-50 rounded-md p-3 border border-blue-200"
        >
          <div class="text-center">
            <div class="text-lg font-semibold text-blue-800">{{ formatDuration(callDurationSeconds) }}</div>
            <div class="text-sm text-blue-600">Call Duration</div>
          </div>
        </div>

        <div v-if="callState === 'active'" class="bg-white rounded-md p-3 border border-gray-200">
          <div class="flex items-center justify-center space-x-4">
            <button
              class="flex flex-col items-center p-3 rounded-lg transition-colors"
              :class="isMuted ? 'bg-red-100 text-red-700' : 'bg-gray-100 text-gray-700 hover:bg-gray-200'"
              @click="onToggleMute"
            >
              <svg class="w-6 h-6 mb-1" fill="none" viewBox="0 0 24 24" stroke="currentColor">
                <path
                  v-if="isMuted"
                  stroke-linecap="round"
                  stroke-linejoin="round"
                  stroke-width="2"
                  d="M5.586 15H4a1 1 0 01-1-1v-4a1 1 0 011-1h1.586l4.707-4.707C10.923 3.663 12 4.109 12 5v14c0 .891-1.077 1.337-1.707.707L5.586 15z"
                  clip-rule="evenodd"
                />
                <path
                  v-else
                  stroke-linecap="round"
                  stroke-linejoin="round"
                  stroke-width="2"
                  d="M15.536 8.464a5 5 0 010 7.072m2.828-9.9a9 9 0 010 12.728M5.586 15H4a1 1 0 01-1-1v-4a1 1 0 011-1h1.586l4.707-4.707C10.923 3.663 12 4.109 12 5v14c0 .891-1.077 1.337-1.707.707L5.586 15z"
                />
              </svg>
              <span class="text-xs">{{ isMuted ? 'Unmute' : 'Mute' }}</span>
            </button>

            <button
              class="flex flex-col items-center p-3 rounded-lg bg-red-100 text-red-700 hover:bg-red-200 transition-colors"
              @click="onHangup"
            >
              <svg class="w-6 h-6 mb-1" fill="none" viewBox="0 0 24 24" stroke="currentColor">
                <path
                  stroke-linecap="round"
                  stroke-linejoin="round"
                  stroke-width="2"
                  d="M16 8l2-2m0 0l2-2m-2 2l-2-2m2 2l2 2M3 3l18 18"
                />
              </svg>
              <span class="text-xs">Hangup</span>
            </button>
          </div>
        </div>
      </div>
    </Modal>
  </div>
</template>

<script setup lang="ts">
import { ref, computed, watch } from 'vue';
import { useCurrentUserStore } from '@/stores/currentUser.ts';
import { useLoadingSpinnerStore } from '@/stores/loadingSpinner.ts';
import { useInAppPhoneCalls } from '@/composables/useInAppPhoneCalls';
import { useAudioDevices } from '@/composables/useAudioDevices';
import { Button } from '@/components/ui/button';
import Modal from './Modal.vue';
import { CallInfoParser } from '@/lib/webrtc/CallInfoParser';
import { webRTCCallSingletonService } from '@/lib/webrtc';
import { toast } from 'vue-sonner';
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from '@/components/ui/select';

type InAppCallModalProps = {
  phoneNumber: string;
  contactName?: string;
  contactId?: number;
};

type InAppCallModalEmits = {
  callEnded: [];
};

const props = defineProps<InAppCallModalProps>();
const emit = defineEmits<InAppCallModalEmits>();

const currentUserStore = useCurrentUserStore();
const loadingSpinnerStore = useLoadingSpinnerStore();
const { callState, callDurationSeconds, makeCall, hangUp, reset } = useInAppPhoneCalls();
const {
  audioInputs,
  audioOutputs,
  selectedMicrophoneId,
  selectedSpeakerId,
  microphoneLevel,
  isMicrophoneLevelActive,
  startMicrophoneLevel,
  stopMicrophoneLevel,
  testSpeaker,
  getConstraintsForMicrophone,
} = useAudioDevices();

const showConfirmModal = ref<boolean>(false);
const isMuted = ref<boolean>(false);

const formattedNumber = computed(() => CallInfoParser.formatE164(props.phoneNumber || ''));

const formatDuration = (seconds: number): string => {
  const minutes = Math.floor(seconds / 60);
  const remainingSeconds = seconds % 60;
  return `${minutes.toString().padStart(2, '0')}:${remainingSeconds.toString().padStart(2, '0')}`;
};

const handleCallClick = () => {
  reset();
  showConfirmModal.value = true;
  startMicrophoneLevel();
};

// Stop microphone level monitoring when modal is closed or call state changes
watch([showConfirmModal, callState], ([modalOpen, state]) => {
  if (!modalOpen || state !== 'idle') {
    stopMicrophoneLevel();
  }
});

const onModalClosed = () => {
  if (callState.value === 'active' || callState.value === 'connecting' || callState.value === 'ringing') {
    onHangup();
  }

  if (callState.value === 'ended') {
    emit('callEnded');
  }

  showConfirmModal.value = false;
  loadingSpinnerStore.hideLocalLoadingSpinner();
};

const confirmStartCall = async () => {
  const teamPhoneNumber = currentUserStore.currentUserAccountDetails.teamPhoneNumber;

  if (!teamPhoneNumber) {
    toast.error('Unable to make call', {
      description: 'Your team does not have a phone number configured',
    });
    return;
  }

  // Pass the selected microphone constraints to the WebRTC service
  await webRTCCallSingletonService.setAudioConstraints(getConstraintsForMicrophone());

  // Set the selected speaker
  await webRTCCallSingletonService.setSpeakerId(selectedSpeakerId.value);

  await makeCall(formattedNumber.value, teamPhoneNumber, props.contactId, currentUserStore.currentUser.id ?? undefined);
};

const getModalTitle = () => {
  switch (callState.value) {
    case 'connecting':
    case 'ringing':
      return 'Connecting Call';
    case 'active':
      return 'Call in Progress';
    case 'ended':
      return 'Call Ended';
    default:
      return 'Start Call';
  }
};

const getModalBody = () => {
  switch (callState.value) {
    case 'connecting':
    case 'ringing':
      return 'Please wait while we connect your call.';
    case 'active':
      return 'Your call is currently active. Use the controls below to manage the call.';
    case 'ended':
      return 'The call has ended.';
    default:
      return 'Are you sure you want to start this call?';
  }
};

const getModalAction = () => {
  switch (callState.value) {
    case 'connecting':
    case 'ringing':
      return 'Cancel Call';
    case 'active':
      return 'End Call';
    case 'ended':
      return 'Close';
    default:
      return 'Start Call';
  }
};

const getModalActionColor = () => {
  switch (callState.value) {
    case 'connecting':
    case 'ringing':
      return 'yellow';
    case 'active':
      return 'red';
    case 'ended':
      return 'gray';
    default:
      return 'indigo';
  }
};

const handleModalAction = async () => {
  try {
    switch (callState.value) {
      case 'connecting':
      case 'ringing':
        onHangup();
        showConfirmModal.value = false;
        loadingSpinnerStore.hideLocalLoadingSpinner();
        break;
      case 'active':
        onHangup();
        showConfirmModal.value = false;
        loadingSpinnerStore.hideLocalLoadingSpinner();
        break;
      case 'ended':
        showConfirmModal.value = false;
        emit('callEnded');
        loadingSpinnerStore.hideLocalLoadingSpinner();
        break;
      default:
        try {
          await confirmStartCall();
          loadingSpinnerStore.hideLocalLoadingSpinner();
        } catch (error) {
          loadingSpinnerStore.hideLocalLoadingSpinner();
          showConfirmModal.value = false;
          toast.error('Failed to start call', {
            description: error instanceof Error ? error.message : 'Unknown error',
          });
        }
        break;
    }
  } catch (error) {
    loadingSpinnerStore.hideLocalLoadingSpinner();
    toast.error('Action failed', {
      description: error instanceof Error ? error.message : 'Unknown error',
    });
  }
};

const onToggleMute = () => {
  isMuted.value = webRTCCallSingletonService.toggleMute();
};

const onHangup = () => {
  hangUp();
};
</script>
